/**
 * Accueil « springboard » — navigation tactile PWA-first (Phase A, 05/08/2026).
 *
 * Point d'entrée pensé iPhone/iPad : grille de grosses tuiles ordonnées selon
 * l'usage réel mesuré (Plausible + nginx + DB, analyse du 05/08/2026), avec
 * badges dynamiques best-effort (réception en attente, alertes réappro,
 * tâches production de la semaine, rayons d'inventaire en retard) et un
 * bandeau « Aujourd'hui » (CA, ventes, panier moyen via /analytics/operational).
 *
 * Page STANDALONE : volontairement hors LayoutV2 (pas de sidebar) — elle ne
 * modifie rien à l'existant et peut être supprimée sans trace. La navigation
 * classique reste accessible via la tuile « Version classique ».
 *
 * Les données se rafraîchissent au retour sur la page (visibilitychange/focus)
 * avec un throttle de 60 s : /pos/restock-alerts/count écrit en base à la
 * lecture (réconciliation), il ne faut pas le marteler.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { productionApi } from '../api/production.api';

interface Badges {
  reception: number | null;
  rayon: number | null;
  reappro: number | null;
  production: number | null;
  inventaire: number | null;
}

interface TodayStats {
  ca_cents: number;
  sales_count: number;
}

interface Tile {
  label: string;
  sub?: string;
  icon: string;
  href: string;
  badge?: keyof Badges;
  accent?: boolean;
  /**
   * Ouvre dans un onglet à part. Réservé aux destinations hors de l'espace
   * équipe : la boutique a sa propre navigation et ne ramène pas ici, or en PWA
   * il n'y a pas de bouton « précédent » — y entrer dans la même fenêtre serait
   * un aller sans retour.
   */
  external?: boolean;
}

/** Badges « attention » (ambre) — le rouge reste réservé à l'urgent. */
// « rayon » compte du stock qui attend, pas une urgence : ambre comme production.
const WARN_BADGES: ReadonlySet<keyof Badges> = new Set(['production', 'inventaire', 'rayon'] as const);

/**
 * Tuiles principales — ordonnées par fréquence d'usage réelle.
 * « Mise en rayon » suit « Réception » : c'est la suite immédiate du geste
 * (la marchandise entre en réserve, puis part au rayon).
 */
const QUOTIDIEN: Tile[] = [
  { label: 'Caisse', sub: 'POS', icon: '🛒', href: '/pos', accent: true },
  { label: 'Réception', sub: 'Bons à réceptionner', icon: '📥', href: '/reception', badge: 'reception' },
  { label: 'Mise en rayon', sub: 'Réserve → rayon au scan', icon: '⬆️', href: '/pos/reception?tab=rayon', badge: 'rayon' },
  { label: 'Réserve', sub: 'Ranger (allée·colonne·étagère)', icon: '🗄️', href: '/storage/putaway' },
  { label: 'Réappro', sub: 'Alertes & commandes', icon: '🔔', href: '/pos/restock-alerts', badge: 'reappro' },
  { label: 'Produits', sub: 'Catalogue & photos', icon: '📦', href: '/products' },
  { label: 'Stocks', sub: 'Réserve & rayon', icon: '📊', href: '/stocks' },
  { label: 'Inventaire', sub: 'Comptages', icon: '🔢', href: '/inventory', badge: 'inventaire' },
];

const PILOTAGE: Tile[] = [
  { label: 'Tableau de bord', icon: '📈', href: '/dashboard' },
  { label: 'Rapports Z', icon: '🧾', href: '/closing-reports' },
  { label: 'Historique ventes', icon: '📜', href: '/sales-history' },
  { label: 'Analytics', icon: '📊', href: '/analytics' },
  // Le suivi des tournées depuis le téléphone (23/10/2026) : l'icône « Folies
  // Équipe » de l'iPhone ouvre cette page, aucune tuile n'y menait.
  { label: 'Livraisons', icon: '🚚', href: '/customer-deliveries' },
  { label: 'Tournées en cours', icon: '📍', href: '/delivery-dashboard' },
];

const AUTRES: Tile[] = [
  { label: 'La boutique', icon: '🛍️', href: '/shop', external: true },
  { label: 'Production', icon: '🌱', href: '/production', badge: 'production' },
  { label: 'Étiquettes', icon: '🏷️', href: '/label-templates' },
  { label: 'Clients', icon: '👥', href: '/customers' },
  { label: 'Réglages', icon: '⚙️', href: '/settings' },
];

function tenantIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('tenant');
    return raw ? (JSON.parse(raw)?.id ?? null) : null;
  } catch {
    return null;
  }
}

/** Semaine ISO courante (même calcul que CropPlanning). */
function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

/** round : sans centimes — le CA du jour à 4 chiffres doit tenir dans 1/3 d'écran 375 px. */
function formatEur(cents: number, opts?: { round?: boolean }): string {
  return (cents / 100).toLocaleString('fr-BE', {
    style: 'currency', currency: 'EUR',
    ...(opts?.round ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  });
}

/** Intervalle minimal entre deux chargements (le count réappro écrit en base). */
const REFRESH_MIN_MS = 60_000;

export default function Accueil() {
  const [badges, setBadges] = useState<Badges>({
    reception: null, rayon: null, reappro: null, production: null, inventaire: null,
  });
  const [stats, setStats] = useState<TodayStats | null>(null);
  const lastLoadRef = useRef(0);

  // Badges + stats best-effort : la page doit rendre même si un appel échoue.
  const load = useCallback(async () => {
    if (Date.now() - lastLoadRef.current < REFRESH_MIN_MS) return;
    lastLoadRef.current = Date.now();

    const results = await Promise.allSettled([
      api.get<{ total: number }>('/pos/restock-alerts/count'),
      (async () => {
        const tid = tenantIdFromStorage();
        if (!tid) return [] as { status: string }[];
        return api.listDeliveries(tid, { status: 'pending', limit: 100 });
      })(),
      api.getOperationalKPIs() as Promise<{ today_ca_cents: number; transactions_today: number }>,
      productionApi.listCropTasks({ week: getISOWeek(new Date()), completed: false }),
      // Via ApiClient (pas posApi) : posApi fait primer customer_token sur auth_token,
      // un staff avec une session eshop de test aurait un 401 silencieux ici.
      api.get<{ overdue: boolean }[]>('/inventory/coverage'),
      // Produits en attente de mise en rayon (stock en réserve).
      (async () => {
        const tid = tenantIdFromStorage();
        if (!tid) return [] as unknown[];
        return api.getBackroomProducts(tid) as Promise<unknown[]>;
      })(),
    ] as const);
    const [alerts, deliveries, kpis, tasks, coverage, backroom] = results;

    // Blip réseau : si TOUT a échoué, rien n'a atteint le serveur — libérer le
    // throttle pour que le prochain retour sur la page recharge sans attendre.
    if (results.every((r) => r.status === 'rejected')) {
      lastLoadRef.current = 0;
      return; // et conserver les dernières valeurs affichées
    }

    // planned_week est une semaine ISO SANS année : on écarte les tâches
    // d'anciens plans restées ouvertes (même semaine, autre année).
    const year = new Date().getFullYear();
    const isCurrentYear = (t: { planned_date?: string | null }) =>
      !t.planned_date || new Date(t.planned_date).getFullYear() === year;

    // Best-effort par indicateur : un appel rejeté garde la dernière valeur connue.
    setBadges((prev) => ({
      reappro: alerts.status === 'fulfilled' ? (alerts.value?.total ?? 0) : prev.reappro,
      reception: deliveries.status === 'fulfilled' ? deliveries.value.length : prev.reception,
      production: tasks.status === 'fulfilled' ? tasks.value.filter(isCurrentYear).length : prev.production,
      inventaire: coverage.status === 'fulfilled'
        ? coverage.value.filter((r) => r.overdue).length
        : prev.inventaire,
      rayon: backroom.status === 'fulfilled' && Array.isArray(backroom.value)
        ? backroom.value.length
        : prev.rayon,
    }));
    if (kpis.status === 'fulfilled') {
      setStats({ ca_cents: kpis.value.today_ca_cents ?? 0, sales_count: kpis.value.transactions_today ?? 0 });
    }
  }, []);

  useEffect(() => {
    document.title = 'Accueil — Folie Boutique';
    load();
    // Rafraîchir quand on revient sur la PWA (iPad qui sort de veille, retour d'onglet).
    const onReturn = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [load]);

  const today = new Date().toLocaleDateString('fr-BE', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const basket = stats && stats.sales_count > 0
    ? Math.round(stats.ca_cents / stats.sales_count)
    : null;

  const renderTile = (t: Tile, big: boolean) => {
    const count = t.badge ? badges[t.badge] : null;
    const warn = t.badge ? WARN_BADGES.has(t.badge) : false;
    return (
      <Link
        key={t.href + t.label}
        to={t.href}
        {...(t.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`relative flex flex-col items-center justify-center gap-1 rounded-2xl border shadow-sm
          transition-transform duration-100 active:scale-95 select-none touch-manipulation
          ${big ? 'h-32 sm:h-36' : 'h-24'}
          ${t.accent
            ? 'bg-[#2d6a4f] border-[#2d6a4f] text-white shadow-md'
            : 'bg-white border-black/[.08] text-gray-800'}`}
      >
        {count !== null && count !== undefined && count > 0 && (
          <span className={`absolute top-2 right-2 min-w-[1.6rem] h-6 px-1.5 flex items-center justify-center
            rounded-full text-sm font-bold shadow
            ${warn ? 'bg-amber-400 text-amber-950' : 'bg-red-600 text-white'}`}>
            {count > 99 ? '99+' : count}
          </span>
        )}
        <span className={big ? 'text-4xl' : 'text-2xl'} aria-hidden>{t.icon}</span>
        <span className={`font-semibold leading-tight text-center px-1 ${big ? 'text-base' : 'text-sm'}`}>
          {t.label}
        </span>
        {big && t.sub && (
          <span className={`text-xs leading-tight text-center px-2 ${t.accent ? 'text-white/75' : 'text-gray-500'}`}>
            {t.sub}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#faf7f0] pb-[max(2.5rem,env(safe-area-inset-bottom))]">
      {/* En-tête */}
      <header className="px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-4 max-w-3xl mx-auto">
        <p className="text-xs font-bold tracking-widest uppercase text-[#2d6a4f]">
          Folie Boutique · Gestion
        </p>
        <h1 className="text-2xl font-bold text-gray-900 capitalize">{today}</h1>
      </header>

      <main className="px-4 max-w-3xl mx-auto space-y-7">
        {/* Aujourd'hui — bandeau best-effort, tape → tableau de bord */}
        <Link
          to="/dashboard"
          className="grid grid-cols-3 gap-3 select-none touch-manipulation active:scale-[.98] transition-transform duration-100"
        >
          {[
            { label: 'CA du jour', value: stats ? formatEur(stats.ca_cents, { round: true }) : '—' },
            { label: 'Ventes', value: stats ? String(stats.sales_count) : '—' },
            { label: 'Panier moyen', value: basket !== null ? formatEur(basket) : '—' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-black/[.08] shadow-sm px-2 py-2.5 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-gray-900 tabular-nums truncate">{s.value}</p>
            </div>
          ))}
        </Link>

        {/* Quotidien */}
        <section>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {QUOTIDIEN.map((t) => renderTile(t, true))}
          </div>
        </section>

        {/* Pilotage */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">
            Pilotage
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PILOTAGE.map((t) => renderTile(t, false))}
          </div>
        </section>

        {/* Autres */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">
            Autres
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {AUTRES.map((t) => renderTile(t, false))}
          </div>
        </section>

        {/* Sortie vers la navigation classique */}
        <footer className="pt-2 text-center">
          <Link to="/dashboard" className="text-sm text-gray-400 underline underline-offset-4">
            Version classique (sidebar)
          </Link>
        </footer>
      </main>
    </div>
  );
}
